import { getUser, hasRole, useJobColors } from "../../core/Global"

/**
 * The console's shared colour vocabulary.
 *
 * The palette is account data, managed at CRM > Base Data > Job Colors: each entry has a name the
 * account chose ("Problem Jobs" rather than "Red"), and the backend seeds nine well-separated hues
 * the first time an account looks at it. Nothing here is hardcoded — renaming a colour on that page
 * renames it in every picker.
 */
export interface ColorItem {
    name: string
    value: string
}

export const isSameColor = (a?: string, b?: string) =>
    !!a && !!b && a.toLowerCase() === b.toLowerCase()

/** The account's name for a colour, falling back to the hex for one that's since been removed. */
export const useColorName = (color?: string) => {
    const palette = usePalette()
    if (!color) return undefined
    return palette.find(f => isSameColor(f.value, color))?.name ?? color.toUpperCase()
}

/** "#abc" / "abc" / "#AABBCC" -> "#AABBCC", or undefined when it isn't a hex colour. */
export const normalizeHex = (input?: string) => {
    if (!input) return undefined
    let v = input.trim().toUpperCase()
    if (!v.startsWith("#")) v = "#" + v
    if (!/^#(?:[0-9A-F]{3}|[0-9A-F]{6})$/.test(v)) return undefined
    if (v.length === 4) v = "#" + v[1] + v[1] + v[2] + v[2] + v[3] + v[3]
    return v
}

/** The account's active job colours, in palette order. */
export const usePalette = (): ColorItem[] =>
    useJobColors()
        .filter(c => !!c.color)
        .map(c => ({ name: c.name ?? c.color!, value: c.color! }))

/**
 * Growing or editing the account palette is an account-wide, permanent change. The account owner
 * and system admins always may; everyone else — a plain Admin included — needs ManageJobColors.
 * Mirrors CanManage() on the JobColors controller.
 */
export const canManageColors = () => {
    const user = getUser()
    if (!user) return false
    if (user.isSuperAdmin || user.isSystemAdmin || user.isCustomer) return true
    return hasRole("ManageJobColors")
}

/** Deleting is gated separately, the same way job statuses and job types are. */
export const canDeleteColors = () => {
    const user = getUser()
    if (!user) return false
    if (user.isSuperAdmin || user.isSystemAdmin || user.isCustomer) return true
    return hasRole("DeleteJobColors")
}
